
/**
 * Debug utility functions for identifying issues in the application
 */

interface LogState {
  isAuthenticated?: boolean;
  loading?: boolean;
  error?: any;
  [key: string]: any; // Allow additional properties
}

// Log auth state change with timestamp and support for additional properties
export const logAuthStateChange = (
  event: string, 
  state: LogState
): void => {
  console.log(
    `[${new Date().toISOString()}] Auth state change (${event}):`, 
    JSON.stringify(state)
  );
};

// Log routing actions with enhanced details
export const logRouting = (
  action: string,
  path: string,
  details?: Record<string, any>
): void => {
  console.log(
    `[${new Date().toISOString()}] Routing (${action}): ${path}`,
    details ? JSON.stringify(details) : ''
  );
};

// Log errors with context and stack traces
export const logError = (
  context: string,
  error: unknown,
  additionalInfo?: Record<string, any>
): void => {
  console.error(
    `[${new Date().toISOString()}] Error in ${context}:`,
    error,
    additionalInfo ? JSON.stringify(additionalInfo) : ''
  );
  
  // Log stack trace if available
  if (error instanceof Error && error.stack) {
    console.error(`Stack trace: ${error.stack}`);
  }
};

// Debug function to extract and log JWT information
export const debugJwtRoles = () => {
  try {
    // Get JWT from localStorage
    const token = localStorage.getItem('supabase.auth.token');
    if (token) {
      const parsed = JSON.parse(token);
      const accessToken = parsed?.currentSession?.access_token || 
                           parsed?.access_token || 
                           parsed?.session?.access_token;
      
      if (accessToken) {
        // Decode JWT payload
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        
        console.group('JWT Debug Information');
        console.log('JWT Role:', payload?.app_metadata?.role);
        console.log('JWT Sub:', payload?.sub);
        console.log('JWT App Metadata:', payload?.app_metadata);
        console.log('JWT User Metadata:', payload?.user_metadata);
        console.log('JWT Expiration:', payload?.exp ? new Date(payload.exp * 1000).toISOString() : 'Not set');
        console.groupEnd();
        
        return {
          role: payload?.app_metadata?.role,
          sub: payload?.sub,
          app_metadata: payload?.app_metadata,
          user_metadata: payload?.user_metadata,
          exp: payload?.exp ? new Date(payload.exp * 1000).toISOString() : null
        };
      }
    }
    
    console.warn('No JWT token found in localStorage');
    return null;
  } catch (error) {
    console.error('Error debugging JWT roles:', error);
    return null;
  }
};

// Debug function to log auth storage state
export const logAuthStorageState = () => {
  try {
    // Get storage key from the Supabase client configuration
    const storageKey = 'supabase.auth.token';
    
    // Check for token in localStorage
    const storedToken = localStorage.getItem(storageKey);
    
    // Find all Supabase-related keys in localStorage
    const supabaseKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.includes('supabase')) {
        supabaseKeys.push(key);
      }
    }
    
    console.log('Local storage auth:', {
      hasToken: !!storedToken,
      tokenLength: storedToken ? storedToken.length : 0
    });
    
    console.log('All Supabase-related localStorage keys:', supabaseKeys);
    
    // Attempt to decode JWT
    if (storedToken) {
      try {
        const session = JSON.parse(storedToken);
        let token = null;
        
        if (session.currentSession?.access_token) {
          token = session.currentSession.access_token;
        } else if (session.access_token) {
          token = session.access_token;
        } else if (session.session?.access_token) {
          token = session.session.access_token;
        }
        
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          console.log('JWT Payload Structure:', {
            sub: payload.sub,
            roleDirectly: payload.role,
            roleInAppMetadata: payload.app_metadata?.role,
            hasAppMetadata: !!payload.app_metadata,
            appMetadataKeys: payload.app_metadata ? Object.keys(payload.app_metadata) : [],
            expiresAt: payload.exp ? new Date(payload.exp * 1000).toISOString() : null
          });
          
          return {
            jwtPayload: payload,
            supabaseKeys
          };
        }
      } catch (e) {
        console.error('Error parsing JWT:', e);
        return { error: e.message };
      }
    }
    
    return { 
      supabaseKeys,
      hasToken: !!storedToken
    };
    
  } catch (e) {
    console.error('Error checking auth storage:', e);
    return { error: e.message };
  }
};

// Monitor API calls to detect patterns in failures
export const createApiCallMonitor = () => {
  const originalFetch = window.fetch;
  const callLog: Array<{
    url: string;
    method: string;
    timestamp: number;
    success: boolean;
    error?: string;
    requestId: string;
  }> = [];
  
  // Replace fetch with monitoring version
  window.fetch = async function(input, init) {
    // Extract URL and method from input and init
    let url: string;
    let method: string;
    
    if (typeof input === 'string') {
      url = input;
      method = init?.method || 'GET';
    } else if (input instanceof Request) {
      url = input.url;
      method = input.method || 'GET';
    } else {
      // For URL objects, convert to string
      url = input.toString();
      method = init?.method || 'GET';
    }
    
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
    
    // Add request ID to headers for correlation
    const headers = new Headers(init?.headers || {});
    headers.append('X-Request-ID', requestId);
    
    // Create a new init object with our headers
    const newInit = {
      ...init,
      headers
    };
    
    console.log(`[API Call] ${method} ${url} [${requestId}]`);
    
    try {
      const response = await originalFetch(input, newInit);
      
      // Log successful requests
      if (response.ok) {
        callLog.push({
          url,
          method,
          timestamp: Date.now(),
          success: true,
          requestId
        });
        return response;
      } 
      
      // Log failed requests
      const errorText = await response.clone().text();
      const error = `Status ${response.status}: ${errorText.substring(0, 100)}${errorText.length > 100 ? '...' : ''}`;
      console.error(`[API Error] Status ${response.status} ${method} ${url} [${requestId}]: ${error}`);
      
      callLog.push({
        url,
        method,
        timestamp: Date.now(),
        success: false,
        error: `Status ${response.status}: ${response.statusText}`,
        requestId
      });
      
      return response;
    } catch (error) {
      // Log network errors
      console.error(`[API Error] Network Error ${method} ${url} [${requestId}]:`, error);
      
      callLog.push({
        url,
        method,
        timestamp: Date.now(),
        success: false,
        error: error.message,
        requestId
      });
      
      throw error;
    }
  };
  
  // Return functions to interact with the monitor
  return {
    getLogs: () => [...callLog],
    getFailures: () => callLog.filter(log => !log.success),
    getSuccesses: () => callLog.filter(log => log.success),
    reset: () => {
      callLog.length = 0;
    },
    restore: () => {
      window.fetch = originalFetch;
    }
  };
};
